/**
 * useLoadingPage - 门店列表加载与懒加载
 *
 * 功能：
 * - 按大区/省/市/品牌/状态等条件过滤酒店列表
 * - 首屏加载前 3 个门店，加载完后若当前页还有未加载的则每次加载 1 个，滚动到底再继续加载
 * - 每个门店独立加载状态（storeLoadingStates Map），更新单店不触发整表重渲染
 * - refreshStore 仅更新 ref 中的 refreshKey，由 StoreDataLoader 检测后触发内部刷新
 *
 * 数据流：getHotelList → allHotels → filteredHotels → hotelListItems
 * 加载流：loadStore(storeId) → storeLoadingStates 更新 → StoreDataLoader 挂载 → useLoadingStoreData 拉数
 */
import { useState, useEffect, useMemo, useCallback, useRef, startTransition } from 'react';
import { getHotelList, type PmsHotelCacheItem } from '../../../service/hotelApi';
import { createLogger } from '../utils/logger';

export interface HotelListItem {
  id: number;
  name: string;
  hotel: PmsHotelCacheItem;
  dataLoaded: boolean; // 是否已加载数据
  loading: boolean; // 是否正在加载数据
}

/** 首屏只加载前 3 个门店，加载完后若当前页还有未加载的则每次加载 1 个，减轻主线程压力 */
const INITIAL_BATCH_SIZE = 3;
const POST_INITIAL_BATCH_SIZE = 1; // 首屏 3 个加载完后，后续每次加载 1 个门店
const INITIAL_BATCH_STAGGER_MS = 150; // 首屏错峰 150ms，减轻后端压力

export interface UseLoadingPageParams {
  searchKeyword?: string;
  regionIds?: number[]; // 大区ID列表（支持多选）
  provinceIds?: number[]; // 省份ID列表（支持多选）
  cityIds?: number[]; // 城市ID列表（支持多选）
  hotelIds?: number[]; // 门店ID列表（支持多选）
  excludeHotelIds?: number[]; // 排除的门店ID（如「除了三元桥店」）
  brandIds?: number[]; // 品牌ID列表（支持多选）
  status?: number; // 状态过滤，默认0（已上线）
  showTestStore?: boolean; // 是否显示测试门店，默认false（不显示）
  /** 当前已渲染的门店数量（displayLimit），滚动加载时只加载此范围内的未加载店，避免加载到尚未挂载的店导致“卡住” */
  displayLimit?: number;
}

/**
 * Hook：管理酒店列表和动态加载
 */
export function useLoadingPage(params?: UseLoadingPageParams) {
  const logger = useMemo(() => createLogger('useLoadingPage'), []);
  const [allHotels, setAllHotels] = useState<PmsHotelCacheItem[]>([]);
  // 注意：loadedHotelIds 和 loadingHotels 现在从 storeLoadingStates 计算得出
  // 不再直接使用 state，而是通过 useMemo 从 storeLoadingStates 计算
  const [loading, setLoading] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 保留 ref 用于其他地方读取（向后兼容）
  const loadedHotelIdsRef = useRef<Set<number>>(new Set());
  const loadingHotelsRef = useRef<Set<number>>(new Set());

  // 根据搜索条件过滤酒店列表
  const filteredHotels = useMemo(() => {
    let list = [...allHotels];

    // 按搜索关键词过滤
    if (params?.searchKeyword) {
      const keyword = params.searchKeyword.toLowerCase();
      list = list.filter((h) => {
        const name = (h.name || h.fullName || '').toLowerCase();
        const code = (h.code || '').toLowerCase();
        return name.includes(keyword) || code.includes(keyword);
      });
    }

    // 按大区过滤（支持多选）
    if (params?.regionIds && params.regionIds.length > 0) {
      list = list.filter((h) => {
        const hr = typeof h.regionId === 'number' ? h.regionId : Number(h.regionId);
        return !Number.isNaN(hr) && params.regionIds!.includes(hr);
      });
    }

    // 按省份过滤（支持多选）
    if (params?.provinceIds && params.provinceIds.length > 0) {
      list = list.filter((h) => {
        const hp = typeof h.province === 'number' ? h.province : Number(h.province);
        return !Number.isNaN(hp) && params.provinceIds!.includes(hp);
      });
    }

    // 按城市过滤（支持多选）
    if (params?.cityIds && params.cityIds.length > 0) {
      list = list.filter((h) => {
        const hc = typeof h.city === 'number' ? h.city : Number(h.city);
        return !Number.isNaN(hc) && params.cityIds!.includes(hc);
      });
    }

    // 按门店过滤（支持多选）
    if (params?.hotelIds && params.hotelIds.length > 0) {
      list = list.filter((h) => {
        return params.hotelIds!.includes(h.id);
      });
    }

    // 排除指定门店（如「除了三元桥店」）
    if (params?.excludeHotelIds && params.excludeHotelIds.length > 0) {
      list = list.filter((h) => !params.excludeHotelIds!.includes(h.id));
    }

    // 按品牌过滤（支持多选）
    if (params?.brandIds && params.brandIds.length > 0) {
      list = list.filter((h) => {
        const hb = typeof h.brandId === 'number' ? h.brandId : Number(h.brandId);
        return !Number.isNaN(hb) && params.brandIds!.includes(hb);
      });
    }

    // 过滤测试门店（hotelid=1），默认不显示
    if (!params?.showTestStore) {
      list = list.filter((h) => h.id !== 1);
    }

    return list;
  }, [
    allHotels,
    params?.searchKeyword,
    params?.regionIds,
    params?.provinceIds,
    params?.cityIds,
    params?.hotelIds,
    params?.excludeHotelIds,
    params?.brandIds,
    params?.showTestStore,
  ]);

  // 使用 Map state 来存储每个门店的加载状态，实现更细粒度的更新
  // 这样当更新某个门店的状态时，只有该门店相关的数据会变化
  // 注意：必须在 hotelListItems 之前定义，因为 hotelListItems 会使用它
  const [storeLoadingStates, setStoreLoadingStates] = useState<Map<number, { loading: boolean; loaded: boolean }>>(new Map());
  const storeLoadingStatesRef = useRef<Map<number, { loading: boolean; loaded: boolean }>>(new Map());

  // 同步 ref 和 state
  useEffect(() => {
    storeLoadingStatesRef.current = storeLoadingStates;
  }, [storeLoadingStates]);

  // 从 storeLoadingStates 生成 loadedHotelIds 和 loadingHotels
  // 这样我们可以使用更细粒度的状态更新
  const loadedHotelIds = useMemo(() => {
    const ids = new Set<number>();
    storeLoadingStates.forEach((state, id) => {
      if (state.loaded) {
        ids.add(id);
      }
    });
    return ids;
  }, [storeLoadingStates]);

  const loadingHotels = useMemo(() => {
    const ids = new Set<number>();
    storeLoadingStates.forEach((state, id) => {
      if (state.loading) {
        ids.add(id);
      }
    });
    return ids;
  }, [storeLoadingStates]);

  // 同步到 ref（用于其他地方读取）
  useEffect(() => {
    loadedHotelIdsRef.current = loadedHotelIds;
  }, [loadedHotelIds]);

  useEffect(() => {
    loadingHotelsRef.current = loadingHotels;
  }, [loadingHotels]);

  // 转换为列表项格式
  // 使用 ref 缓存上一次的结果，只在真正需要时才创建新数组
  // 关键：使用 storeLoadingStates 的序列化来检测变化，实现更细粒度的更新
  const prevHotelListItemsRef = useRef<HotelListItem[]>([]);
  const prevStoreLoadingStatesRef = useRef<string>('');
  const prevFilteredHotelsRef = useRef<PmsHotelCacheItem[]>([]);

  // 将 storeLoadingStates 序列化为字符串，用于比较
  const storeLoadingStatesKey = useMemo(() => {
    return JSON.stringify(Array.from(storeLoadingStates.entries()).sort((a, b) => a[0] - b[0]));
  }, [storeLoadingStates]);

  const hotelListItems = useMemo<HotelListItem[]>(() => {
    // 检查 filteredHotels 是否变化
    const filteredChanged =
      prevFilteredHotelsRef.current.length !== filteredHotels.length ||
      prevFilteredHotelsRef.current.some((h, i) => h.id !== filteredHotels[i]?.id);

    // 检查 storeLoadingStates 是否变化
    const loadingStatesChanged = prevStoreLoadingStatesRef.current !== storeLoadingStatesKey;

    // 如果 filteredHotels 和 storeLoadingStates 都没变，返回缓存
    if (!filteredChanged && !loadingStatesChanged) {
      return prevHotelListItemsRef.current; // 返回缓存的引用
    }

    // 需要更新，创建新数组
    const newItems = filteredHotels.map((hotel) => {
      const name = hotel.name || hotel.fullName || `门店 ${hotel.id}`;
      const state = storeLoadingStates.get(hotel.id);
      return {
        id: hotel.id,
        name,
        hotel,
        dataLoaded: state?.loaded || false,
        loading: state?.loading || false,
      };
    });

    // 更新缓存
    prevFilteredHotelsRef.current = [...filteredHotels];
    prevStoreLoadingStatesRef.current = storeLoadingStatesKey;
    prevHotelListItemsRef.current = newItems;

    return newItems;
  }, [filteredHotels, storeLoadingStates, storeLoadingStatesKey]);

  // 当过滤条件改变时，重置已加载的酒店ID（因为过滤后的列表可能不同）
  // 使用 useMemo 稳定依赖项，避免频繁重置
  // 对数组进行排序，确保相同内容生成相同的 key
  const normalizeArray = useCallback((arr: number[] | undefined): string => {
    if (!arr || arr.length === 0) return '';
    return [...arr].sort((a, b) => a - b).join(',');
  }, []);

  const regionIdsKey = useMemo(() => normalizeArray(params?.regionIds), [normalizeArray, params?.regionIds]);
  const provinceIdsKey = useMemo(() => normalizeArray(params?.provinceIds), [normalizeArray, params?.provinceIds]);
  const cityIdsKey = useMemo(() => normalizeArray(params?.cityIds), [normalizeArray, params?.cityIds]);
  const hotelIdsKey = useMemo(() => normalizeArray(params?.hotelIds), [normalizeArray, params?.hotelIds]);
  const excludeHotelIdsKey = useMemo(() => normalizeArray(params?.excludeHotelIds), [normalizeArray, params?.excludeHotelIds]);
  const brandIdsKey = useMemo(() => normalizeArray(params?.brandIds), [normalizeArray, params?.brandIds]);

  const filterKey = useMemo(() => {
    return JSON.stringify({
      searchKeyword: params?.searchKeyword || '',
      regionIds: regionIdsKey,
      provinceIds: provinceIdsKey,
      cityIds: cityIdsKey,
      hotelIds: hotelIdsKey,
      excludeHotelIds: excludeHotelIdsKey,
      brandIds: brandIdsKey,
      status: params?.status ?? 0,
    });
  }, [
    params?.searchKeyword,
    regionIdsKey,
    provinceIdsKey,
    cityIdsKey,
    hotelIdsKey,
    excludeHotelIdsKey,
    brandIdsKey,
    params?.status,
  ]);

  // 使用 ref 存储上一次的 filterKey，只在真正变化时才重置
  const prevFilterKeyRef = useRef<string>('');

  useEffect(() => {
    // 只有当 filterKey 真正变化时才重置（避免相同内容但引用不同导致的重复重置）
    if (prevFilterKeyRef.current !== filterKey) {
      logger.debug('过滤条件变化，重置加载状态', {
        prev: prevFilterKeyRef.current,
        current: filterKey,
      });
      prevFilterKeyRef.current = filterKey;
      // 清空已加载的酒店ID，重新开始加载
      setStoreLoadingStates(new Map());
      setInitialBatchLoaded(false); // 重置初始批次标记
    }
  }, [filterKey, logger]);

  // 页面加载时先获取列表：缺省为全部门店（已上线），只拉名称不拉详情；列表渲染容器占位，不加载门店数据
  // 大区/省/市等过滤在前端做，后端只按 status 返回全量，避免多端口径不一致
  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const queryParams = {
      pageNum: 1,
      pageSize: 300, // 全部门店数量上限，避免一次拉上万条导致卡顿
      status: params?.status ?? 0, // 缺省 0=已上线，即全部门店（不含已下线/待上线）
      // 不传 regionId，前端支持多选大区，后端单 regionId；先拉全量再前端过滤
    };
    logger.debug('请求参数（缺省全部门店）', queryParams);

    getHotelList(queryParams)
      .then((resp) => {
        if (!mounted) return;
        logger.debug('响应数据', {
          code: resp.code,
          total: resp.data?.total,
          listLength: resp.data?.list?.length,
        });
        if (resp.code === 1 && resp.data?.list) {
          startTransition(() => {
            setAllHotels(resp.data.list);
            setStoreLoadingStates(new Map());
          });
          logger.info(`已加载酒店列表（全部门店），数量: ${resp.data.list.length}，容器占位不加载数据`);
        } else {
          logger.warn('响应异常', resp);
          startTransition(() => setAllHotels([]));
        }
      })
      .catch((err) => {
        logger.error('加载酒店列表失败', err);
        if (mounted) startTransition(() => setAllHotels([]));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [logger, params?.status]); // 只依赖 status，不依赖 regionIds

  // 是否已加载初始批次
  const [initialBatchLoaded, setInitialBatchLoaded] = useState(false);

  // 使用 ref 存储 hotelListItems 的最新值，避免在 loadNextBatch 中依赖它
  const hotelListItemsRef = useRef<HotelListItem[]>([]);
  useEffect(() => {
    hotelListItemsRef.current = hotelListItems;
  }, [hotelListItems]);

  // 加载单个门店数据（手动触发或懒加载触发）
  // 每个门店的数据完全独立，加载一个门店不会影响其他门店
  // 关键：更新 state 触发 StoreDataLoader 组件渲染，StoreDataLoader 会调用 useLoadingStoreData 从后端获取数据
  const loadStore = useCallback((storeId: number) => {
    // 检查是否已加载或正在加载（使用 ref 检查）
    const currentState = storeLoadingStatesRef.current.get(storeId);
    if (currentState?.loaded || currentState?.loading) {
      logger.debug(`门店 ${storeId} 已加载或正在加载，跳过`);
      return;
    }

    logger.info(`开始加载门店: ${storeId}（将触发后端接口调用）`, {
      note: '更新状态后会触发 StoreDataLoader 渲染，StoreDataLoader 使用 useLoadingStoreData 从后端获取数据',
      trigger: '懒加载或手动加载',
    });

    // 先更新 ref
    const newMap = new Map(storeLoadingStatesRef.current);
    newMap.set(storeId, { loading: true, loaded: false });
    storeLoadingStatesRef.current = newMap;

    // 非紧急更新用 startTransition，优先响应菜单点击等用户操作
    startTransition(() => {
      setStoreLoadingStates((prev) => {
        const prevState = prev.get(storeId);
        if (!prevState || !prevState.loading) {
          const updatedMap = new Map(prev);
          updatedMap.set(storeId, { loading: true, loaded: false });
          return updatedMap;
        }
        return prev;
      });
    });

    // 延迟更新为已加载状态，给 StoreDataLoader 时间开始加载数据
    setTimeout(() => {
      const finalMap = new Map(storeLoadingStatesRef.current);
      finalMap.set(storeId, { loading: false, loaded: true });
      storeLoadingStatesRef.current = finalMap;

      startTransition(() => {
        setStoreLoadingStates((prev) => {
          const prevState = prev.get(storeId);
          const newState = finalMap.get(storeId);
          if (prevState?.loaded !== newState?.loaded || prevState?.loading !== newState?.loading) {
            logger.debug(`门店 ${storeId} 状态已更新为已加载，StoreDataLoader 将开始从后端获取数据`);
            return finalMap;
          }
          return prev;
        });
      });
    }, 50);
  }, [logger]);

  // 批量加载门店数据（用于初始加载 / 滚动加载）
  // 滚动时只加载「当前已渲染范围」内的未加载店，避免加载到尚未挂载的店导致请求不发、界面卡住
  const loadNextBatch = useCallback((isInitial = false) => {
    const currentItems = hotelListItemsRef.current;
    const limit = params?.displayLimit ?? currentItems.length;

    logger.debug(`开始批量加载，当前列表长度: ${currentItems.length}, displayLimit: ${limit}`, {
      isInitial,
    });

    // 只考虑当前已渲染范围内的门店（前 displayLimit 个），确保要加载的店已挂载 StoreDataLoader
    const itemsInView = isInitial ? currentItems : currentItems.slice(0, limit);
    const unloadedItems = itemsInView.filter((item) => {
      const state = storeLoadingStatesRef.current.get(item.id);
      return !state?.loaded && !state?.loading;
    });

    if (unloadedItems.length === 0) {
      logger.debug('没有更多数据需要加载');
      return; // 没有更多数据需要加载
    }

    // 初始加载：取前 INITIAL_BATCH_SIZE 个；非初始（首屏 3 个加载完后继续补全或滚动）每次只加载 1 个，减轻主线程阻塞
    const batchSize = isInitial
      ? Math.min(INITIAL_BATCH_SIZE, unloadedItems.length)
      : Math.min(POST_INITIAL_BATCH_SIZE, unloadedItems.length);
    const batch = unloadedItems.slice(0, batchSize);
    const batchIds = batch.map((item) => item.id);

    logger.info(`开始批量加载门店`, batchIds, {
      isInitial,
      batchSize,
      totalUnloaded: unloadedItems.length,
      note: '每个门店数据完全独立',
    });

    // 首屏错峰：依次延迟触发，避免 3 个请求同时打满后端树加载槽位，加载时间更稳定
    if (isInitial && batchIds.length > 1) {
      batchIds.forEach((storeId, index) => {
        const delay = index * INITIAL_BATCH_STAGGER_MS;
        if (delay === 0) {
          loadStore(storeId);
        } else {
          setTimeout(() => loadStore(storeId), delay);
        }
      });
    } else {
      batchIds.forEach((storeId) => loadStore(storeId));
    }

    if (isInitial) {
      setInitialBatchLoaded(true);
      logger.debug(`初始批次加载完成，已标记 initialBatchLoaded = true`);
    }
  }, [loadStore, logger, params?.displayLimit]);

  // 列表就绪后，自动开始加载前 INITIAL_BATCH_SIZE 个门店的数据与组件；之后由滚动触发加载更多
  useEffect(() => {
    const shouldLoadInitial =
      hotelListItems.length > 0 &&
      loadedHotelIds.size === 0 &&
      !loading &&
      loadingHotels.size === 0 &&
      !initialBatchLoaded;

    logger.debug('检查初始加载条件', {
      hotelListItemsLength: hotelListItems.length,
      loadedHotelIdsSize: loadedHotelIds.size,
      loading,
      loadingHotelsSize: loadingHotels.size,
      initialBatchLoaded,
      shouldLoadInitial,
      hotelListItems: hotelListItems.slice(0, 5).map(item => ({ id: item.id, name: item.name })),
    });

    if (shouldLoadInitial) {
      logger.info(`列表已展示，开始加载前 ${INITIAL_BATCH_SIZE} 个门店的数据与组件，门店总数: ${hotelListItems.length}`);
      const timer = setTimeout(() => {
        loadNextBatch(true); // 首屏加载前 N 个（3 或 5），错峰请求
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [hotelListItems, loading, loadedHotelIds.size, loadingHotels.size, initialBatchLoaded, loadNextBatch, logger]);

  // 注意：loadedHotelIdsRef 和 loadingHotelsRef 已经在上面通过 useEffect 同步了

  // 存储每个门店的刷新键，用于触发 useLoadingStoreData 重新加载
  // 使用 ref 存储刷新键，完全避免触发全局重新渲染
  // 关键：只更新 ref，不更新任何 state
  const storeRefreshKeysRef = useRef<Map<number, number>>(new Map());

  // 刷新单个门店数据（只影响该门店，不影响其他门店）
  // 注意：这个函数只更新 ref，不更新任何 state，所以不会触发全局重新渲染
  // 实际的刷新由 StoreDataLoader 组件内部的 refresh 函数完成
  const refreshStore = useCallback((storeId: number | string) => {
    const id = typeof storeId === 'string' ? Number.parseInt(storeId, 10) : storeId;
    if (Number.isNaN(id)) {
      logger.warn('无效的门店ID', storeId);
      return;
    }

    logger.info(`刷新门店数据: ${id}（只影响该门店，不影响其他门店）`);

    // 更新刷新键（只更新该门店的键，不影响其他门店）
    // 只更新 ref，不更新 state，完全避免触发全局重新渲染
    const currentKey = storeRefreshKeysRef.current.get(id) || 0;
    const newKey = currentKey + 1;
    storeRefreshKeysRef.current.set(id, newKey);

    logger.debug('刷新键已更新', `${id} -> ${newKey}`, {
      note: '只更新 ref，不更新任何 state，完全避免触发全局重新渲染',
      otherStoresUnchanged: '其他门店的 refreshKey 不变，不会重新渲染',
      nextStep: 'StoreDataLoader 组件会检测到 refreshKey prop 变化并重新渲染',
    });
  }, [logger]);

  // 返回 storeRefreshKeys getter 函数
  // 使用 ref 读取，不依赖 state，避免触发全局重新渲染
  const getStoreRefreshKey = useCallback((storeId: number): number => {
    return storeRefreshKeysRef.current.get(storeId) || 0;
  }, []);

  // 懒加载：监听滚动事件，在距离底部 500px 时触发加载
  // 使用 ref 存储 loadNextBatch，避免依赖变化导致重新绑定事件
  const loadNextBatchRef = useRef(loadNextBatch);
  useEffect(() => {
    loadNextBatchRef.current = loadNextBatch;
  }, [loadNextBatch]);

  // 懒加载：使用滚动事件 + Intersection Observer 双重机制
  // 使用 useRef 存储事件处理器，确保能正确清理
  const scrollHandlerRef = useRef<(() => void) | null>(null);
  // 使用 ref 存储加载标志，避免闭包问题
  const isLoadingRef = useRef(false);
  // Intersection Observer 实例
  const observerRef = useRef<IntersectionObserver | null>(null);
  // 使用 ref 存储 loadStore，避免依赖问题
  const loadStoreRef = useRef(loadStore);
  useEffect(() => {
    loadStoreRef.current = loadStore;
  }, [loadStore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      // ref 还未设置，等待下次渲染
      return;
    }

    // 如果已经有事件处理器，先移除旧的
    if (scrollHandlerRef.current) {
      container.removeEventListener('scroll', scrollHandlerRef.current);
      scrollHandlerRef.current = null;
    }

    // 清理旧的 Intersection Observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    logger.debug('滚动容器已准备好，绑定懒加载事件');

    // 防抖定时器
    let scrollTimer: NodeJS.Timeout | null = null;

    // 检查并加载下一个门店的函数
    const checkAndLoadNext = () => {
      if (isLoadingRef.current) {
        return;
      }

      const currentContainer = scrollContainerRef.current;
      if (!currentContainer) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = currentContainer;
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;

      // 距离底部 700px 时触发加载
      if (distanceToBottom <= 700) {
        // 检查是否还有未加载的门店（使用 ref 获取最新状态）
        const currentItems = hotelListItemsRef.current;
        const unloadedItems = currentItems.filter((item) => {
          const state = storeLoadingStatesRef.current.get(item.id);
          return !state?.loaded && !state?.loading;
        });

        if (unloadedItems.length > 0) {
          isLoadingRef.current = true;
          logger.debug(`触发懒加载，距离底部: ${distanceToBottom.toFixed(0)}px，未加载门店数: ${unloadedItems.length}`, {
            unloadedStoreIds: unloadedItems.map(item => item.id),
            note: '将调用后端接口获取这些门店的数据',
          });

          // 使用 ref 调用，避免依赖问题
          loadNextBatchRef.current(false);

          // 1000ms 后重置加载标志，允许下次加载
          setTimeout(() => {
            isLoadingRef.current = false;
            logger.debug('加载标志已重置，可以继续加载');
          }, 1000);
        }
      }
    };

    const handleScroll = () => {
      // 清除之前的定时器
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }

      // 防抖：200ms 后执行
      scrollTimer = setTimeout(() => {
        checkAndLoadNext();
      }, 200);
    };

    // 保存事件处理器引用，用于清理
    scrollHandlerRef.current = handleScroll;

    // 绑定滚动事件
    container.addEventListener('scroll', handleScroll, { passive: true });
    logger.debug('滚动事件已绑定到容器');

    // 不再用 Intersection Observer 触发 loadStore，避免首屏/滚动时多店同时进入视口导致大量 data + operation-analysis 同时 pending（仅由 loadNextBatch 控制：首屏 3 店错峰，滚动到底再加载 1 店）
    observerRef.current = null;

    // 初始检查一次：只通过 checkAndLoadNext 触发首屏 3 店加载（loadNextBatch(true) 已在 useEffect 里触发，此处兜底）
    const initialCheckTimer = setTimeout(() => {
      const currentContainer = scrollContainerRef.current;
      if (currentContainer) {
        const { scrollTop, scrollHeight, clientHeight } = currentContainer;
        const distanceToBottom = scrollHeight - scrollTop - clientHeight;
        logger.debug('执行初始懒加载检查', {
          scrollHeight: scrollHeight.toFixed(0),
          clientHeight: clientHeight.toFixed(0),
          distanceToBottom: distanceToBottom.toFixed(0),
        });
        checkAndLoadNext();
      } else {
        logger.warn('初始检查时容器不存在');
      }
    }, 500);

    const containerForCleanup = scrollContainerRef.current;
    return () => {
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
      if (initialCheckTimer) {
        clearTimeout(initialCheckTimer);
      }
      if (containerForCleanup && scrollHandlerRef.current) {
        containerForCleanup.removeEventListener('scroll', scrollHandlerRef.current);
        scrollHandlerRef.current = null;
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [hotelListItems.length, logger]); // 依赖 hotelListItems.length，当列表变化时重新绑定

  return {
    hotelListItems,
    loading,
    scrollContainerRef,
    loadNextBatch,
    loadStore, // 新增：手动加载单个门店
    refreshStore,
    getStoreRefreshKey, // 返回 getter 函数，避免直接返回 Map 导致重新渲染
    totalCount: filteredHotels.length,
    loadedCount: loadedHotelIds.size,
  };
}
